import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import client from '../../api/client';
import ProductCard from '../../components/ProductCard/ProductCard';
import ProductDetail from '../../components/ProductDetail';
import SearchBar from '../../components/SearchBar';
import MergeRequestBanner from '../../components/MergeRequestBanner';
import { CARE_LEVEL_OPTIONS } from '../../constants/careLevels';

// Заглушка категорий если API недоступен
const FALLBACK_CATEGORIES = [
  { id: '1', name: 'Плодовые деревья', slug: 'fruit-trees' },
  { id: '2', name: 'Плодовые кустарники', slug: 'fruit-bushes' },
  { id: '3', name: 'Хвойные растения', slug: 'conifers' },
  { id: '4', name: 'Колоновидные', slug: 'columnar' },
  { id: '5', name: 'Декоративные', slug: 'decorative' },
  { id: '6', name: 'Однолетние', slug: 'annuals' },
  { id: '7', name: 'Многолетние', slug: 'perennials' },
  { id: '8', name: 'Рассада', slug: 'seedlings' },
  { id: '9', name: 'Сопутствующие', slug: 'accessories' },
  { id: '10', name: 'Грунты', slug: 'soil' },
];

export default function Catalog() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deepLinkProduct, setDeepLinkProduct] = useState(null);
  const [storeName, setStoreName] = useState('So.Va');
  const [storeLogo, setStoreLogo] = useState('/logo.png');
  const [storeSubtitle, setStoreSubtitle] = useState('Садовый центр и Питомник растений');
  const [careFilter, setCareFilter] = useState('');
  // Панель фильтра: категории и уход прячутся за кнопкой рядом с поиском
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sort, setSort] = useState('');
  const activeFilterCount =
    (selectedCategory ? 1 : 0) +
    (careFilter ? 1 : 0) +
    (priceMin ? 1 : 0) +
    (priceMax ? 1 : 0) +
    (sort ? 1 : 0);

  // Проверяем параметр product в URL (для кнопки из бота)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('product');
    if (productId) {
      // Загружаем товар по ID
      client.get(`/catalog/products/${productId}`)
        .then(res => {
          if (res.data) {
            setDeepLinkProduct(res.data);
          }
        })
        .catch(() => {})
        .finally(() => {
          // Убираем параметр из URL чтобы не открывался повторно
          navigate(window.location.pathname, { replace: true });
        });
    }
  }, [navigate]);

  // Загружаем настройки магазина
  useEffect(() => {
    client.get('/settings/app')
      .then(res => {
        if (res.data.store_name) setStoreName(res.data.store_name);
        if (res.data.store_logo) setStoreLogo(res.data.store_logo);
        if (res.data.store_subtitle) setStoreSubtitle(res.data.store_subtitle);
      })
      .catch(() => {});
  }, []);

  // Загружаем категории
  useEffect(() => {
    client.get('/catalog/categories')
      .then(res => {
        if (res.data && res.data.length > 0) {
          setCategories(res.data);
        }
      })
      .catch(err => {
        console.warn('Категории не загружены, используем заглушку:', err.message);
      });
  }, []);

  // Загружаем товары (с отменой предыдущего запроса)
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const params = {};
    if (selectedCategory) {
      params.category = selectedCategory;
    }
    if (searchQuery) params.search = searchQuery;
    if (careFilter) params.care = careFilter;
    if (priceMin) params.price_min = priceMin;
    if (priceMax) params.price_max = priceMax;
    if (sort) params.sort = sort;

    client.get('/catalog/products', { params, signal: controller.signal })
      .then(res => {
        const items = res.data.items || [];
        setProducts(items);
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== 'CanceledError') {
          console.error('Ошибка загрузки товаров:', err);
          setProducts([]);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [selectedCategory, searchQuery, careFilter, priceMin, priceMax, sort]);

  // Определяем название активной категории
  const activeCategory = categories.find(c => c.slug === selectedCategory);
  const activeCategoryName = activeCategory ? activeCategory.name : 'Все товары';

  return (
    <div className="pb-4 animate-fade-in">
      {/* Шапка */}
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="mb-3 flex items-center gap-2">
          <img src={storeLogo} alt={storeName} className="h-10 w-10 rounded-xl object-contain" />
          <div>
            <div className="text-base font-bold text-green-dark">{storeName}</div>
            <div className="-mt-0.5 text-[11px] uppercase tracking-widest text-green-muted">
              {storeSubtitle}
            </div>
          </div>
        </div>
        <div className="flex items-stretch gap-2">
          <div className="min-w-0 flex-1">
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen(v => !v)}
            aria-expanded={filtersOpen}
            aria-controls="catalog-filters"
            className={`flex shrink-0 items-center gap-1.5 rounded-2xl border px-3.5 text-sm font-medium transition active:scale-95 ${
              activeFilterCount > 0
                ? 'border-green-dark bg-green-dark text-cream'
                : 'border-border-warm bg-cream-card text-green-dark hover:border-green-sage hover:bg-green-sage/20'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Фильтр
            {activeFilterCount > 0 && (
              <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-green-primary px-1 text-[11px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Запрос на объединение аккаунтов */}
      <MergeRequestBanner className="mx-4 mt-3" />

      {/* Фильтры — как на сайте: Категория, Уход, Цена, Сортировка */}
      <div
        id="catalog-filters"
        className="mx-4 mt-3 space-y-3 rounded-2xl bg-cream-card p-3.5 ring-1 ring-border-warm"
        hidden={!filtersOpen}
      >
        {/* Сброс — как «Очистить» в Избранном/Корзине: справа сверху, при активных фильтрах */}
        {activeFilterCount > 0 && (
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => {
                setSelectedCategory(null);
                setCareFilter('');
                setPriceMin('');
                setPriceMax('');
                setSort('');
              }}
              className="text-sm text-green-muted hover:text-[#D64545]"
            >
              Сбросить
            </button>
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-green-muted">
            Категория
          </span>
          <div className="relative">
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="w-full appearance-none rounded-xl border border-border-warm bg-white px-3 py-2.5 pr-9 text-sm text-green-dark outline-none transition focus:border-green-sage"
            >
              <option value="">Все категории</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-muted" />
          </div>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-green-muted">
            Уход
          </span>
          <div className="relative">
            <select
              value={careFilter}
              onChange={(e) => setCareFilter(e.target.value)}
              className="w-full appearance-none rounded-xl border border-border-warm bg-white px-3 py-2.5 pr-9 text-sm text-green-dark outline-none transition focus:border-green-sage"
            >
              <option value="">Любой уход</option>
              {CARE_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-muted" />
          </div>
        </label>

        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-green-muted">
            Цена, ₽
          </span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="от"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className="w-full rounded-xl border border-border-warm bg-white px-3 py-2.5 text-sm text-green-dark outline-none transition focus:border-green-sage"
            />
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="до"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="w-full rounded-xl border border-border-warm bg-white px-3 py-2.5 text-sm text-green-dark outline-none transition focus:border-green-sage"
            />
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-green-muted">
            Сортировка
          </span>
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full appearance-none rounded-xl border border-border-warm bg-white px-3 py-2.5 pr-9 text-sm text-green-dark outline-none transition focus:border-green-sage"
            >
              <option value="">По умолчанию</option>
              <option value="new">Новинки</option>
              <option value="price_asc">Цена: по возрастанию</option>
              <option value="price_desc">Цена: по убыванию</option>
              <option value="name_asc">Название: А–Я</option>
              <option value="name_desc">Название: Я–А</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-muted" />
          </div>
        </label>
      </div>

      {/* Список товаров */}
      <div className="px-4">
        <h2 className="mb-3 text-lg font-bold text-green-dark">
          {activeCategoryName}
          <span className="ml-2 text-sm font-normal text-green-muted">
            · {products.length}
          </span>
        </h2>

        {loading ? (
          <div className="rounded-2xl bg-cream-card py-16 text-center text-sm text-green-muted">
            Загрузка...
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 auto-rows-fr">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl bg-cream-card py-16 text-center text-sm text-green-muted">
            <p className="text-4xl mb-2">🌱</p>
            <p>Товары не найдены</p>
            <p className="mt-1 text-xs">Попробуйте изменить поиск или категорию</p>
          </div>
        )}
      </div>

      {/* Глубокая ссылка из бота — открываем товар */}
      {deepLinkProduct && (
        <ProductDetail
          product={deepLinkProduct}
          onClose={() => setDeepLinkProduct(null)}
        />
      )}
    </div>
  );
}
